import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';

import { AppProvider } from 'contexts/appContext';
import { HistoryProvider } from 'hooks';
import { jobService } from 'services/jobService';
import { jobListingHash } from 'store/jobs/actions';

import '../../utils/nativeUtils';

import { useStore } from '../store';
import { checkLocalStorage, getCookie, loadServiceWorker, sendAnalytics } from '../utils';
import { GOOGLE_AD_SENSE_PUBLIC_KEY,HIDE_GOOGLE_ADS_ROUTES } from '../utils/constant';
import Header from '../widgets/header';

import '../css/application.css';
import '../less/less.css';
import '../components/index.css';
import '../layouts/index.css';
import '../widgets/index.css';
import 'styles/app.global.scss';

const Footer = dynamic(() => import('../widgets/Footer'));

const leftPanelUICookie = '_zippia-leftPanelUI';
const paidFixedPosCookie = '_zippia-paidFixedPos';
const joblistVsCarouselCookie = '_zippia-joblistVsCarousel';

declare global {
  interface Window {
    userAgent: string;
    pageCategory: string;
    GA_INITIALIZED: boolean;
    btoa: (value: string) => string;
  }
}

if (!global.window) {
  global.window = {
    navigator: {
      userAgent: 'node',
    },
    userAgent: 'node',
    location: {
      protocol: 'http:',
      hostname: 'localhost',
      port: '8000',
    },
  } as any;
}

const isServer = typeof window === 'undefined';

if (isServer) {
  console.log = () => {};
}

if (typeof document === 'undefined') {
  global.document = {
    querySelector: () => {},
  } as any;
}

window.btoa = (str) => Buffer.from(str).toString('base64');

export function reportWebVitals(metric: NextWebVitalsMetric) {
  switch (metric.name) {
    case 'FCP':
      sendAnalytics(metric);
      break;
    case 'LCP':
      sendAnalytics(metric);
      break;
    case 'CLS':
      sendAnalytics(metric);
      break;
    case 'FID':
      sendAnalytics(metric);
      break;
    case 'TTFB':
      sendAnalytics(metric);
      break;
    case 'Next.js-hydration':
      sendAnalytics(metric);
      break;
    case 'Next.js-route-change-to-render':
      sendAnalytics(metric);
      break;
    case 'Next.js-render':
      sendAnalytics(metric);
      break;
    default:
      break;
  }
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const store = useStore(pageProps, router);
  const [state, setState] = useState({
    showNewJobSearchUI: false,
    enablePaidFixedPos: false,
    showMiniLeftPanelUI: false,
  });

  checkLocalStorage();

  useEffect(() => {
    async function fetchListingHashes() {
      try {
        const listingHashes = await jobService.getUserlistinghashes();
        store.dispatch(jobListingHash(listingHashes));
      } catch (err) {
        console.log('Error occurred while fetching listing hashes');
      }
    }

    function setCookiesUsingUserGroups() {
      const userGroupLeftPanelUI = getCookie(leftPanelUICookie);
      const userGroupPaidFixedPos = getCookie(paidFixedPosCookie);
      const userGroupMiniLeftPanelUI = getCookie(joblistVsCarouselCookie);

      let showNewJobSearchUI = false;
      let enablePaidFixedPos = false;
      let showMiniLeftPanelUI = false;

      if (userGroupLeftPanelUI === 'groupB') {
        showNewJobSearchUI = true;
      }

      if (userGroupPaidFixedPos === 'groupB') {
        enablePaidFixedPos = true;
      }

      if (userGroupMiniLeftPanelUI === 'groupB') {
        showMiniLeftPanelUI = true;
      }

      setState((state) => ({
        ...state,
        showNewJobSearchUI,
        enablePaidFixedPos,
        showMiniLeftPanelUI,
      }));
    }

    fetchListingHashes();

    setCookiesUsingUserGroups();

    window.addEventListener('load', loadServiceWorker);
    return () => {
      window.removeEventListener('load', loadServiceWorker);
    };
  }, []);

  const hideGoogleAd = HIDE_GOOGLE_ADS_ROUTES.some((route) => router?.asPath?.startsWith(route));

  const hideFooter = pageProps?.data?.notShowFooter || pageProps?.data?.notShowHeaderAndFooter;

  return (
    <HistoryProvider>
      <Provider store={store}>
        <Head>
          <meta
            name="viewport"
            content="height=device-height,
                        width=device-width, initial-scale=1.0,
                        minimum-scale=1.0, maximum-scale=1.0,
                        user-scalable=no, target-densitydpi=device-dpi, viewport-fit=cover"
          />
        </Head>

        {!hideGoogleAd && (
          <Script
            async
            id="Adsense-id"
            crossOrigin="anonymous"
            strategy="afterInteractive"
            onError={(e) => {
              console.error('Script failed to load', e);
            }}
            src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${GOOGLE_AD_SENSE_PUBLIC_KEY}`}
          />
        )}

        {pageProps.data && !pageProps.data.notShowHeaderAndFooter && <Header {...pageProps.data} />}

        <AppProvider value={state}>
          <Component {...pageProps} />
        </AppProvider>

        {pageProps.data && !hideFooter && <Footer />}
      </Provider>
    </HistoryProvider>
  );
}
